import { InputNumber } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedNumber, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Div } from 'ui';
import { v4 as uuidv4 } from 'uuid';
import Table from '../../../../common/table/table';
import { Icons, RowIconAction } from '../../../../common/table/table-helper';
import SearchPart from '../../../../order/parts/components/search-part';
// eslint-disable-next-line max-len
import { clearPartField, updateParts } from '../../../containers/smart-action-sidepanel/smart-action-sidepanel-actions';
import {
  IconWrapper,
  InfoRow,
  StyledInfoIcon,
  StyledRow,
} from './styles';
import { action, handleUpdate } from './tab-helpers';

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  padding-left: 0px;
`;

const QuantityWrapper = styled(Div)`
  .ant-input-number {
    height: 25px !important;

    input {
      height: 22px !important;
    }
  }
`;

const PartTableTab = ({
  intl,
  renderSaveButton,
  disabled,
  form,
}) => {
  const [selectedMaterials, setSelectedMaterials] = useState(null);
  const [materialNumbers, setMaterialNumbers] = useState(null);
  const { parts } = useSelector(state => state.smartAction);
  const { decimalMask } = useSelector(state => state.serviceCenter);
  const dispatch = useDispatch();

  const handleDispatch = (updatedParts) => {
    dispatch(updateParts(
      updatedParts,
    ));
  };

  const getColumns = () => [
    {
      id: 'actions',
      displayName: '',
      fieldName: '',
      width: '16px',
      renderFn: r => <Icons>
        <RowIconAction
          title={intl.formatMessage({ id: 'order.tooltip.deletePart' })}
          tooltipKey='delete'
          iconKey='trash-alt'
          action={
            () => {
              handleDispatch(
                handleUpdate({
                  data: parts,
                  actionType: action.singleDelete,
                  id: r.id,
                }),
              );
            }
          }
          disabled={disabled}
        />
      </Icons>,
    },
    {
      id: 'materialNumber',
      displayName: intl.formatMessage({ id: 'order.parts.MATERIAL_NO' }),
      fieldName: 'materialNumber',
      width: '150px',
      tooltip: row => row.materialNumber,
    },
    {
      displayName: intl.formatMessage({ id: 'order.parts.MATERIAL_DESC' }),
      fieldName: 'description',
      id: 'description',
      width: '300px',
      tooltip: row => row.description,
    },
    {
      id: 'quantity',
      displayName: intl.formatMessage({ id: 'order.parts.QTY_ORDERED' }),
      fieldName: 'quantity',
      width: '120px',
      renderFn: (part) => {
        const quantityValidator = form.getFieldDecorator(part.id, {
          initialValue: part.quantity,
          rules: [{
            required: true,
            message: intl.formatMessage({ id: 'rules.QUANTITY' }),
          }],
        });

        return <QuantityWrapper onClick={e => e.stopPropagation()}>
          {quantityValidator(
            <StyledInputNumber
              min={1}
              disabled={disabled}
            />,
          )}
        </QuantityWrapper>;
      },
    },
    {
      id: 'cost',
      displayName: intl.formatMessage({ id: 'order.parts.COST' }),
      fieldName: 'cost',
      width: '80px',
      tooltip: (part) => {
        if (!Number.isFinite(part.cost)) {
          return null;
        }

        if (part.costCurrency) {
          const number = intl.formatNumber(part.cost, {
            style: 'currency', //eslint-disable-line
            currency: part.costCurrency,
          });
          if (!decimalMask) {
            return number.replace(/\.00$/, '').replace(/^(\D+)/, '$1 ');
          }
          return number.replace(/^(\D+)/, '$1 ');
        }
        return <FormattedNumber value={part.cost} />;
      },
      renderFn: (part) => {
        if (!Number.isFinite(part.cost)) {
          return null;
        }

        if (part.costCurrency) {
          const number = intl.formatNumber(part.cost, {
            style: 'currency', //eslint-disable-line
            currency: part.costCurrency,
          });
          if (!decimalMask) {
            return number.replace(/\.00$/, '').replace(/^(\D+)/, '$1 ');
          }
          return number.replace(/^(\D+)/, '$1 ');
        }
        return <FormattedNumber value={part.cost} />;
      },
    },
    {
      id: 'weight',
      displayName: intl.formatMessage({ id: 'order.parts.WEIGHT' }),
      fieldName: 'weight',
      width: '100px',
      renderFn: part => `${part.weight}  ${part.weightUnit ? part.weightUnit : ''}`,
    },
  ];

  return (
    <>
      {!disabled ? <InfoRow>
        <IconWrapper>
          <StyledInfoIcon type='info-square' size='lg' onClick={e => e.preventDefault()} />
          {intl.formatMessage({ id: 'ticket.smart_actions.ADD_PARTS' })}
        </IconWrapper>
      </InfoRow> :
        null
      }
      <StyledRow>
        <Table
          labelInColumn={true}
          columns={getColumns()}
          disabled={disabled}
          data={parts}
          renderCustomFooter={() => <SearchPart
            widthSearchField={18}
            widthButton={6}
            disabled={disabled}
            handleAddPart={
              (materials) => {
                const materialsDetails = materials?.map(m =>
                ({
                  ...m,
                  addToOrder: true,
                  id: `${m?.id}_${uuidv4()}`,
                }),
                );
                handleDispatch(
                  handleUpdate({
                    data: parts,
                    actionType: materialsDetails?.length === 1 ?
                      action.singleAdd : action.multipleAdd,
                    newItem: materialsDetails?.length === 1 ?
                      // we get a array of materials back, if it is only one, send it as object
                      materialsDetails[0] : materialsDetails,
                  }),
                );
                dispatch(clearPartField(true));
              }
            }
            selectedMaterials={selectedMaterials}
            setSelectedMaterials={setSelectedMaterials}
            materialNumbers={materialNumbers}
            setMaterialNumbers={setMaterialNumbers}
          />}
        />
      </StyledRow>
      {renderSaveButton()}
    </>
  );
};

PartTableTab.propTypes = {
  intl: PropTypes.object,
  renderSaveButton: PropTypes.func,
  disabled: PropTypes.bool,
  form: PropTypes.object,
};

export default injectIntl(PartTableTab);
